import {
  InjectionKey, inject, provide,
} from 'vue';
import { EthereumNetwork, OAuthGrantType } from '@manifoldxyz/frontend-provider-types';

type PropDefinition = {
  type: any; // eslint-disable-line
  default: any; // eslint-disable-line
  required?: boolean;
};

type PropsDefinition = Record<string, PropDefinition>;

/*
 * @dev:
 *
 * Adding a new prop? Declare it in two places:
 *   1. MBurnRedeemCompleteWidgetPropsDefinition with type and default
 *   2. MBurnRedeemCompleteWidgetProps (theres an obvious pattern)
 *
 * Optionally, enforce any new prop-dependency rules in useProvideAll() hook.
 */
export const MBurnRedeemCompleteWidgetPropsDefinition: PropsDefinition = {
  // the Studio Apps Instance ID
  id: {
    type: String,
    default: '',
  },
  // the data required to call getBurnRedeem() on chain
  extensionAddress: {
    type: String,
    default: '',
  },
  redeemContractAddress: {
    type: String,
    default: '',
  },
  burnRedeemIndex: {
    type: String,
    default: '',
  },
  // props for our inter-widget dependencies (connect widget, identity widget)
  fallbackProvider: {
    type: String,
    default: undefined,
    required: false,
  },
  network: {
    type: Number,
    default: EthereumNetwork.MAINNET,
    required: true,
  },
  // clientId: {
  //   type: String,
  //   default: ''
  // },
  // appName: {
  //   type: String,
  //   default: ''
  // },
  // grantType: {
  //   type: String,
  //   default: OAuthGrantType.SIGNATURE
  // }
};

export type MBurnRedeemCompleteWidgetPropsDefinitionType =
  keyof typeof MBurnRedeemCompleteWidgetPropsDefinition;

/*
 * Defined once and used to ensure provide/inject calls are properly typed throughout.
 *
 * @dev: When adding new prop follow the default pattern & define it's JS type.
 */
export class MBurnRedeemCompleteWidgetProps {
  id: string = MBurnRedeemCompleteWidgetPropsDefinition.id.default;

  extensionAddress: string = MBurnRedeemCompleteWidgetPropsDefinition.extensionAddress.default;
  redeemContractAddress: string =
    MBurnRedeemCompleteWidgetPropsDefinition.redeemContractAddress.default;
  burnRedeemIndex: string = MBurnRedeemCompleteWidgetPropsDefinition.burnRedeemIndex.default;

  fallbackProvider: string | undefined =
    MBurnRedeemCompleteWidgetPropsDefinition.fallbackProvider.default;
  network: EthereumNetwork = MBurnRedeemCompleteWidgetPropsDefinition.network.default;
  clientId: string = MBurnRedeemCompleteWidgetPropsDefinition.clientId.default;
  appName: string = MBurnRedeemCompleteWidgetPropsDefinition.appName.default;
  grantType: OAuthGrantType = MBurnRedeemCompleteWidgetPropsDefinition.grantType.default;
}

export const MBurnRedeemCompleteWidgetPropsInjectionKey: InjectionKey<MBurnRedeemCompleteWidgetProps> =
  Symbol('props');

/*
 * A hook you should use to provide all passed in props to the top level component
 * of this widget to its children. After this call is made, all children of that
 * parent component can then use useInjectTopLevelProps() hook to reference those props
 * without needing to prop-weave.
 *
 * ex:
 * const props = defineProps(MBurnRedeemCompleteWidgetPropsDefinition);
 * useProvideAll(props as MBurnRedeemCompleteWidgetProps);
 * // now children can useInjectTopLevelProps() to retrieve provided data
 */
export const useProvideAll = (props: MBurnRedeemCompleteWidgetProps): void => {
  if ((props.appName || props.clientId) && !(props.appName && props.clientId)) {
    throw new Error('Props app-name and client-id must be provided together');
  }

  if (!props.network) {
    throw new Error('Prop network must be provided if fallbackProvider is provided');
  }

  if (
    (props.extensionAddress || props.burnRedeemIndex || props.redeemContractAddress) &&
    !(props.extensionAddress && props.burnRedeemIndex && props.redeemContractAddress)
  ) {
    throw new Error(
      'Props extension-address, redeeem-contract, and burn-index are mutually required',
    );
  }

  if (
    props.id &&
    (props.extensionAddress || props.burnRedeemIndex || props.redeemContractAddress)
  ) {
    throw new Error(
      'Prop data-id negates the need or use for extension-address, redeeem-contract, and burn-index. Do not provide them.',
    );
  }

  if (
    !props.id &&
    !props.extensionAddress &&
    !props.burnRedeemIndex &&
    !props.redeemContractAddress
  ) {
    throw new Error(
      'Widget requires either id OR extension-address, redeeem-contract, and burn-index to be filled out.',
    );
  }

  provide(MBurnRedeemCompleteWidgetPropsInjectionKey, props);
};

/*
 * A hook you can use to get all of the props provided to the top level component
 * of this widget while maintaining type safety.
 *
 * ex:
 * const { id } = useInjectTopLevelProps() // id will be typed
 */
export const useInjectTopLevelProps = (): MBurnRedeemCompleteWidgetProps => {
  const retObj = inject(MBurnRedeemCompleteWidgetPropsInjectionKey);
  if (!retObj) {
    throw new Error('called useInjectTopLevelProps without useProvideAll call in parent component');
  }

  return retObj;
};
